import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './LessonPreView.module.scss';
import FSBackIcon from 'src/assets/icons/arrow_back.svg?react';
import Button from '@src/components/button/Button';
import LessonSidebar from './LessonSidebar';
import SelectedContent from '@src/components/lesson-details/selected-content/SelectedContent';
import { SllLesson, SllModule } from '@src/api/graphql-codegen';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { useStandaloneContent } from '@src/hooks/useStandaloneContent';
import { useAnalytics, Events, useUser } from '@hooks';


type Props = {
  lesson: SllLesson;
  contents: SllModule[];
  onClose: () => void;
  onExit?: () => void;
};

function QuizPreviewComponent(props: Props) {
  const {
    lesson,
    contents,
    onClose,
    onExit,
  } = props;
  // const [showBanner, setShowBanner] = useState(false)
  const {
    content: selectedContent,
    isPreview, setIsPreview,
  } = useStandaloneContent();

  const [showMenu, setShowMenu] = useState(false);
  const [screenWidth, setScreenWidth] = useState(0)
  const [preview, setPreview] = useState(false);

  const isTablet = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent)
  }, []);
  const isPhone = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const regexs = [/(Android)(.+)(Mobile)/i, /BlackBerry/i, /iPhone|iPod/i, /Opera Mini/i, /IEMobile/i]
    return regexs.some((b) => userAgent.match(b))
  }, [])

  const { trackEvent } = useAnalytics();
  const [user] = useUser();

  useEffect(() => {
    if (lesson && user) {
      setIsPreview(true);
      setPreview(true);
      trackEvent(Events.actions.standaloneLessonPreviewOpen, {
        lesson_name: lesson?.name,
        lesson_id: lesson?.id,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson?.id, user, setIsPreview])

  useEffect(() => {
    const listener = () => {
      setTimeout(function () {
        const previewWrap = document.getElementById('previewWrapper')
        if (isPhone && previewWrap) {
          if (isPreview) {
            setPreview(previewWrap.clientHeight > previewWrap.clientWidth)
          }
          setScreenWidth(-previewWrap.clientWidth)
        } else {
          setScreenWidth((isTablet || isPhone) ? -window.innerWidth : 0)
        }
      }, 100);
    };
    window.addEventListener('orientationchange', listener);

    listener();
    return () => {
      window.removeEventListener('orientationchange', listener);
    };
  }, [isTablet, isPhone, isPreview])

  const handleMenu = useCallback(() => {
    if (isTablet || isPhone) {
      setShowMenu(current => !current)
    } else {
      onClose()
    }
  }, [isTablet, isPhone, onClose]);

  const listStyle = useMemo(() => {
    if (isTablet || isPhone) {
      return styles.listMenu;
    } else {
      return styles.list;
    }
  }, [isTablet, isPhone])

  const handleCloseMenu = () => {
    setShowMenu(false)
  }

  const listSection = useMemo(() =>
    (<div className={listStyle} style={{transform: showMenu ? 'translateX(0)' : `translateX(${screenWidth}px)`}}>
      { (isTablet || isPhone) && showMenu && <div className={styles.menuBar}>
        <button onClick={handleMenu} className={styles.menuButton} type={'button'}>
          <div className={styles.menuWrapper}>
            <CloseOutlined className={styles.burgerIcon}/>
          </div>
        </button>
        <button onClick={onClose} className={styles.closeButton} type={'button'}>
          <div className={styles.menuWrapper}>
            <FSBackIcon className={styles.closeIcon}/>{!isPhone && <div>Go Back</div>}
          </div>
        </button>
      </div>}
      {isPhone && <div className={styles.mobileLandscapeCloseBar}>
        <button onClick={handleMenu} className={styles.landscapeCloseButton} type={'button'}>
          <div className={styles.menuWrapper}>
            <FSBackIcon className={styles.closeIcon}/>
          </div>
        </button>
        <div className={styles.title}>{lesson.name}</div>
      </div>}
      {isPhone ? <div className={styles.moduleListView}>
        <div className={styles.moduleList}>
          <LessonSidebar lesson={lesson as SllLesson} quizContents={contents} closeNav={handleCloseMenu} startLesson={()=>{}} isPreProgramQuiz={true} />
        </div>  
        <div className={styles.actions}>
          <Button label='EXIT LESSON' size='small' className={styles.exitLesson} onClick={onClose}/>
        </div>
      </div> : <LessonSidebar lesson={lesson as SllLesson} quizContents={contents} closeNav={handleCloseMenu} startLesson={()=>{}} isPreProgramQuiz={true} />}
    </div>),
    [handleMenu, isTablet, lesson, listStyle, onClose, screenWidth, showMenu, isPhone, contents]
  );

  return (
    <div id="previewWrapper" className={`${styles.container} ${isPhone ? styles.wholeContainer : ''}`}>
      {<div className={isPhone ? styles.mobileTitleView : styles.titleView }>
        {(isTablet || isPhone) 
        ? isPhone && showMenu ? 
          <button onClick={handleMenu} className={styles.closeButton} type={'button'}>
            <div className={styles.menuWrapper}>
              <FSBackIcon className={styles.closeIcon}/>
            </div>
          </button> :
        <button onClick={handleMenu} className={styles.menuButton} type={'button'}>
          <div className={styles.menuWrapper}>
            <MenuOutlined className={styles.burgerIcon}/>
          </div>
        </button>
        : null}
        {!isPhone && <button onClick={onClose} className={styles.closeButton} type={'button'}>
          <div className={styles.menuWrapper}>
            <FSBackIcon className={styles.closeIcon}/>
          </div>
        </button>}
        <div className={styles.titleSubView}>
          <div className={styles.quizTitle}>{lesson.name}</div>
        </div>
      </div>}
      <div className={styles.page} style={{borderTop: '1px solid #D8D8D8'}}>
        {(isTablet || isPhone) && showMenu && <div className={styles.fsBackdrop} onClick={() => setShowMenu(false)}/>}
        {listSection}
        <div className={styles.content}>
          {isPhone &&
            <div className={styles.mobileLandscapeMenuBar}>
              <button onClick={handleMenu} className={styles.landscanpeMenuButton} type={'button'}>
                <MenuOutlined className={styles.landscapeBurgerIcon}/>
              </button>
            </div>
          }
          {selectedContent && <SelectedContent content={selectedContent} lesson={lesson} goBack={onClose} onExit={onExit} isPreview={preview} showBanner={false} />}
        </div>
      </div>
    </div>
  );
}

const QuizPreview = (props: Props) => {

  const {
    lesson,
    contents,
    onClose,
    onExit,
  } = props;

  return (
    <QuizPreviewComponent
      lesson={lesson} contents={contents} onClose={onClose} onExit={onExit} 
    />
  );
}

export default QuizPreview;
